import { IIndicators } from "../../../types/Indicators.type";
import {
  ApiResponseType,
  makeRequest,
} from "../../../common/helpers/api.helpers";
import { createDebounce } from "../../../common/helpers/debounce.helper";
import { getCompanyId } from "../../../common/services/company.api";

const API_URL = `${import.meta.env.VITE_API_URL}/indicators`;

const makeRequestDebounced = createDebounce(makeRequest, 200);

export async function getIndicators({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = "",
  orderBy = "-updatedAt",
  fields = "name, email, cpf, pendencies, type, updatedAt, createdAt, companyId",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IIndicators[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getIndicatorById(indicatorId: string) {
  const url = `${API_URL}/${indicatorId}`;

  const result = makeRequestDebounced(url, {}) as Promise<
    ApiResponseType<IIndicators>
  >;

  return result;
}

export async function createIndicator(indicatorData) {
  const data = {
    ...indicatorData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators>
  >;
}

type PayedIndicator = {
  _id: string | undefined;
  cpfFromIndicated: string;
  payed: string;
};

export async function payedIndicator(data: PayedIndicator) {
  const url = `${API_URL}/${data._id}/payed`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators>
  >;
}

export async function updateIndicatorsProps(
  id: string | undefined,
  updatedData: Partial<IIndicators>
) {
  const url = `${API_URL}/${id}/payments-uploads`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators>
  >;

  return result;
}

const removeReadonlyFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
  delete updatedData.contacts;
  delete updatedData.pendencies;
  delete updatedData.status;
  delete updatedData.updatedAt;
  delete updatedData.createdAt;
};

export async function updateIndicator<T>(id: string, updatedData: T) {
  // remove readonly fields
  removeReadonlyFields(updatedData);

  const url = `${API_URL}/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators>
  >;
  return result;
}

export async function getRentalPoints() {
  const url = `${API_URL}/rentalPoints`;
  const options = {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  };
  const result = makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators[]>
  >;

  return result;
}
export async function deleteIndicator(id: string) {
  const url = `${API_URL}/${id}`;
  const options = {
    method: "DELETE",
  };
  const result = makeRequest(url, options);

  return result;
}

export async function sdrIndicator(data: Partial<IIndicators>) {
  const url = `${API_URL}/${data._id}/sdr`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IIndicators>
  >;
}

export async function getReferralReports({
  page,
  itemsPerPage,
  search,
}: {
  page: number;
  itemsPerPage: number;
  search?: string;
}) {
  const url = `${API_URL}/referral-report?page=${page}&itemsPerPage=${itemsPerPage}&search=${search ?? ""
    }`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = (await makeRequestDebounced(url, {})) as any;

  if (result.ok && result.data?.data) {
    result.data.data = result.data.data.filter((i: IIndicators) => i.cpf);
  }

  return result;
}

export async function getReferralReportsId(id: string) {
  const url = `${API_URL}/referral-report/${id}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = (await makeRequestDebounced(url, {})) as any;
  if (result.ok && result.data?.data) {
    result.data.data = result.data.data.filter((i: IIndicators) => i.cpf);
  }

  return result;
}

export async function getReferralReportsPending({
  search = "",
  page = 1,
  itemsPerPage = "10",
  orderBy = "-updatedAt",
  fields = "name, email, cpf",
  startDate = "", 
  endDate = ""
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("startDate", startDate);
  endDate && params.append("endDate", endDate);

  const url = `${API_URL}/referral-report/pending?${params.toString()}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = (await makeRequestDebounced(url, {})) as any & {
    totalItems: number;
  };
  return result;
}
